import {PropsWithChildren} from "react";
import {Breadcrumb, Button, Result} from "antd";
import {Link, useParams, useSearchParams} from "react-router-dom";
import {createContext} from "../../../../../context/create-context";
import {FullPageLoader} from "../../../../../components/full-page-loader";
import {DashboardWrapper} from "../../../../../layouts/dashboard-wrapper";
import {useGetSubscriptionApi} from "../../../../../react-query-hooks/store/subscriptions/get-subscription";
import type {GetSubscriptionApiResponse} from "../../../../../react-query-hooks/store/subscriptions/get-subscription/types";

type GetEditableSubscriptionState = {
  subscriptionData: GetSubscriptionApiResponse;
  isFetchingSubscription: boolean;
  isLoadingSubscription: boolean;
};

type GetEditableSubscriptionContextState = {
  state: GetEditableSubscriptionState;
};

const [useContext, ContextProvider] =
  createContext<GetEditableSubscriptionContextState>();

export const useGetEditableSubscriptionContext = useContext;

export const GetEditableSubscriptionProvider = ({children}: PropsWithChildren) => {
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const subscriptionId = Number(id);
  const activeTab = searchParams.get("activeTab");
  const isSalesAndInsightsTab = activeTab === "salesAndInsights";
  const pageTitle = isSalesAndInsightsTab ? "Sales and insights" : "Edit subscription";
  const getSubscriptionQuery = useGetSubscriptionApi(
    Number.isFinite(subscriptionId) && subscriptionId > 0 ? subscriptionId : undefined
  );

  if (getSubscriptionQuery.isLoading) {
    return (
      <DashboardWrapper pageTitle={pageTitle}>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/products?activeTab=subscriptions">Subscription</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{pageTitle}</Breadcrumb.Item>
        </Breadcrumb>{" "}
        <FullPageLoader />
      </DashboardWrapper>
    );
  }

  if (getSubscriptionQuery.isError || !getSubscriptionQuery.data) {
    return (
      <DashboardWrapper pageTitle={pageTitle}>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/products?activeTab=subscriptions">Subscription</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{pageTitle}</Breadcrumb.Item>
        </Breadcrumb>{" "}
        <Result
          status="500"
          title="Sorry, something went wrong."
          subTitle="Error fetching subscription"
          extra={
            <Button
              type="primary"
              size="small"
              onClick={() => getSubscriptionQuery.refetch()}
            >
              Try again
            </Button>
          }
        />
      </DashboardWrapper>
    );
  }

  const state: GetEditableSubscriptionState = {
    subscriptionData: getSubscriptionQuery.data,
    isFetchingSubscription: getSubscriptionQuery.isFetching,
    isLoadingSubscription: getSubscriptionQuery.isLoading,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
